import { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderGlobal from "../../components/Header/HeaderGlobal/index.js";
import LogoutModal from "../../components/Header/LogoutModal/index.js";
import NavigationItems from "../../components/Header/NavigationItems/index.js";
import {
    Header,
    HeaderContainer,
    HeaderGlobalBar,
    HeaderName,
    SkipToContent,
} from '@carbon/react';
import LanguageSelector from "../../components/Header/LanguageSelector/index.js";
import { 
    addModal, 
    closeModal, 
    toggleModal, 
    selectModals, 
    selectWrittenNavigationItems, 
    setActiveNavItem, 
    setNavItemVisibility
} from "../../features/application/applicationSlice.js";
import { logoutThunk, profileRequestThunk } from "../../features/user/userThunks.js";
import { selectUserIsAdmin, selectUserProfile, selectUser } from "../../features/user/userSlice.js";
import {
    selectBrandDisplayName,
    selectBrandLogoUrl,
} from "../../features/branding/brandingSlice.js";
import { getModalById } from "../../utils/modalUtils.js";
import { useTranslation } from "react-i18next";
import { selectFilterConfig, selectFilterValues } from "../../features/dashboard/dashboardSlice.js";
import { downloadDashboardReport } from "../../utils/reportUtils.js";

const HeaderLayout = () => {
    const { t, i18n } = useTranslation();
    const navigator = useNavigate();
    const location = useLocation()
    const dispatch = useDispatch()
    const store = useStore()
    const dispatchLogout = () => {
        dispatch(closeModal(modalLogout))
        dispatch(logoutThunk())
        navigator('/login', { replace: true })
    }
    const user = useSelector(selectUser)
    const brandDisplayName = useSelector(selectBrandDisplayName)
    const brandLogoUrl = useSelector(selectBrandLogoUrl)
    const isAdmin = useSelector(selectUserIsAdmin)
    const userProfile = useSelector(selectUserProfile)
    const modal = useSelector(selectModals)
    const navItems = useSelector(selectWrittenNavigationItems)
    const modalLogout = 'logout'
    const [reportLoading, setReportLoading] = useState(false)
    const filterConfig = useSelector(selectFilterConfig)
    const filterValues = useSelector(selectFilterValues)

    const downloadReport = async () => {
        setReportLoading(true)
        try {
            await downloadDashboardReport({
                filterConfig,
                filterValues,
                store,
                language: i18n.resolvedLanguage || i18n.language,
            })
        } catch (error) {
            console.error("Unable to generate report", error)
            window.alert(t("header.reportError"))
        } finally {
            setReportLoading(false)
        }
    }

    useEffect(() => {
        dispatch(addModal(modalLogout))
    }, [dispatch])

    useEffect(() => {
        if (user.token_access && !userProfile?.user) {
            dispatch(profileRequestThunk())
        }
    }, [user.token_access, userProfile?.user, dispatch])

    useEffect(() => {
        dispatch(setNavItemVisibility({ item: 'admin', active: isAdmin }))
    }, [isAdmin, dispatch])

    useEffect(() => {
        const pathToItemMap = {
            '/dashboard': 'dashboard',
            '/telemetry': 'telemetry',
            '/profile': 'profile',
            '/notifications': 'notifications',
            '/login': 'login'
        }
        
        const item = pathToItemMap[location.pathname]
        if (item) {
            dispatch(setActiveNavItem({ item }))
        }
    }, [location.pathname, dispatch])

    const redirectTo = (item) => {
        if (item.target === 'in') {
            navigator(item.url)
        } else {
            window.location.href = `${window.location.origin}${item.url}`
        }
    }

    const highlightElement = (item_name, label) => {
        const activeItem = navItems.find(item => item.name === item_name && item.now === true)
        if (activeItem) {
            return <span className="nav-active"><strong><u>{label}</u></strong></span>
        }
        return <span>{label}</span>
    }
    const brandName = brandDisplayName || 'MakeMake'
    const brandMark = (
        <span className="brand-mark">
            {brandLogoUrl && (
                <img
                    className="brand-mark__logo"
                    src={brandLogoUrl}
                    alt={brandName}
                    onError={(event) => {
                        event.currentTarget.style.display = 'none'
                    }}
                />
            )}
            <span className="brand-mark__name">{brandName}</span>
        </span>
    )

    if (location.pathname !== '/' && location.pathname !== '/login') {
        return (
            <HeaderContainer
                render={() => (
                    <Header aria-label={brandName}>
                        <SkipToContent>{t("app.skipToContent")}</SkipToContent>
                        <HeaderName
                            className="pointer"
                            href="/dashboard"
                            onClick={(event) => {
                                event.preventDefault()
                                navigator('/dashboard')
                            }}
                            prefix=""
                        >
                            {brandMark}
                            <span className={'subtitle'}>{t("app.subtitle")}</span>
                        </HeaderName>
                        <NavigationItems 
                            onItemClick={redirectTo} 
                            highlightElement={highlightElement} 
                            onReportClick={downloadReport}
                            reportLoading={reportLoading}
                        />
                        <HeaderGlobal 
                            onProfileClick={redirectTo} 
                            onNotificationsClick={redirectTo} 
                            onLogoutClick={() => dispatch(toggleModal(modalLogout))}
                        />
                        <LogoutModal 
                            isOpen={getModalById(modal, modalLogout) ? getModalById(modal, modalLogout).state : false}
                            onClose={() => dispatch(closeModal(modalLogout))}
                            onLogout={dispatchLogout}
                        />
                    </Header>)}
            />
        )
    }
    else {
        return (
            <HeaderContainer
                render={() => (
                    <Header aria-label={brandName}>
                        <SkipToContent>{t("app.skipToContent")}</SkipToContent>
                        <HeaderName
                            className="pointer"
                            href="/login"
                            onClick={(event) => {
                                event.preventDefault()
                                navigator('/login')
                            }}
                            prefix=""
                        >
                            {brandMark}
                            <span className={'subtitle'}>{t("app.subtitle")}</span>
                        </HeaderName>
                        <HeaderGlobalBar className="mm-header-global">
                            <LanguageSelector />
                        </HeaderGlobalBar>
                    </Header>)}
            />
        )
    }
}
export default HeaderLayout
